<template>
  <v-dialog v-model="dialogOpen" max-width="860px">
    <v-card>
      <v-card-title class="tw-mb-5">
        <span>{{ isEditMode ? "แก้ไขข้อมูลผู้จําหน่าย" : "เพิ่มบริษัทผู้จําหน่าย" }}</span>
      </v-card-title>
      <v-card-text>
        <v-form ref="apVendorForm" v-model="valid">
          <v-container>
            <!-- Vendor Information -->
            <v-row class="py-0">
              
              <v-col cols="12" sm="3" class="py-0">
                <v-text-field
                  v-model="form.venCode"
                  :label="venCodeLabel"
                  :rules="[v => !!v || venCodeRequiredMessage]"
                  required
                  dense
                  outlined
                  persistent-placeholder
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2" class="py-0">
                <v-text-field
                  v-model="form.venPrefix"
                  :rules="[v => !!v || 'กรุณากรอกคำนำหน้า']"
                  label="คำนำหน้า"
                  dense
                  outlined
                  persistent-placeholder
                ></v-text-field>
              </v-col>
              <v-col cols="10" sm="5" class="py-0">
                <v-text-field
                  v-model="form.venName"
                  :label="venNameLabel"
                  :rules="[v => !!v || venNameRequiredMessage]"
                  required
                  dense
                  outlined
                  persistent-placeholder
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2" class="py-0">
                <v-text-field
                  v-model="form.venBranch"
                  :rules="[v => !!v || 'กรุณากรอกสาขา']"
                  label="สาขา"
                  dense
                  outlined
                  persistent-placeholder
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row class="py-0">
              <v-col cols="12" class="py-0">
                <v-textarea
                  v-model="form.venAddr"
                  label="ที่อยู่"
                  :rules="[v => !!v || 'กรุณากรอกที่อยู่']"
                  required
                  rows="2"
                  dense
                  persistent-placeholder
                  outlined
                ></v-textarea>
              </v-col>
            </v-row>
            <v-row class="py-0">
              <v-col cols="12" sm="5" class="py-0">
                <v-text-field
                  v-model="form.venTaxId"
                  label="เลขประจำตัวผู้เสียภาษี"
                  :rules="[v => !!v || 'กรุณากรอกเลขประจำตัวผู้เสียภาษี']"
                  required
                  dense
                  persistent-placeholder
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3" class="py-0">
                <v-text-field
                  v-model="form.venZipCode"
                  label="รหัสไปรษณีย์"
                  :rules="[v => !!v || 'กรุณากรอกรหัสไปรษณีย์']"
                  persistent-placeholder
                  dense
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" class="py-0">
                <v-autocomplete
                  v-model="form.payCode"
                  :items="paymentList"
                  :item-text="pay => `${pay.payCode} - ${pay.payName}`"
                  item-value="payCode"
                  label="วิธีการจ่ายชําระ (Payment method)"
                  :rules="[v => !!v || 'กรุณาเลือกรหัสบัญชี']"
                  required
                  dense
                  outlined
                  persistent-placeholder
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row class="tw-flex tw-gap-8 tw-px-4">
                <v-switch color="error"  v-model="form.isRejected" label="ละเว้นผู้จำหน่ายรายนี้"></v-switch>
                <v-switch color="primary" v-model="form.isIncludeVat" label="รวมภาษี"></v-switch>
                <v-switch color="info" v-model="form.isForeign" label="ต่างประเทศ"></v-switch>
            </v-row>

            <!-- Accounting Information -->
            <v-divider class="my-4"></v-divider>
            <div class="mb-10 tw-text-base"><span class="tw-text-red-500 tw-opacity-80">*</span> สําหรับรหัสภาษี P07</div>
            <v-row class="py-0">
              <v-col cols="12" sm="6" class="py-0">
                <v-autocomplete
                  v-model="form.defaultAcctP07.expCode"
                  :items="expenseList"
                  :item-text="exp => `${exp.expCode} - ${exp.expName}`"
                  item-value="expCode"
                  label="รหัสบัญชีรายจ่าย (Expense Code)"
                  :rules="[v => !!v || 'กรุณาเลือกรหัสบัญชี']"
                  required
                  dense
                  outlined
                  persistent-placeholder
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6" class="py-0">
                <v-autocomplete
                  v-model="form.defaultAcctP07.vatCode"
                  :items="vatList"
                  :item-text="vat => `${vat.vatCode} - ${vat.vatName}`"
                  item-value="vatCode"
                  label="รหัสภาษี (Tax Code)"
                  :rules="[v => !!v || 'กรุณาเลือกรหัสภาษี']"
                  disabled
                  required
                  dense
                  outlined
                  persistent-placeholder
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row class="py-0">
              <v-col cols="12" sm="6" class="py-0">
                <v-autocomplete
                  v-model="form.defaultAcctP07.whtCode"
                  :items="whtList"
                  :item-text="wht => `${wht.whtCode} - ${wht.whtName} (${wht.whtRate}%)`"
                  item-value="whtCode"
                  label="รหัสภาษีหัก ณ ที่จ่าย (WTax Code)"
                  dense
                  outlined
                  persistent-placeholder
                ></v-autocomplete>
              </v-col>
              <v-col v-if="form.isForeign" cols="12" sm="6" class="py-0">
                <v-autocomplete
                  :items="[{text: '-', value: ''}]"
                  item-text="text"
                  item-value="value"
                  label="รหัส ภ.พ. 36 (PP36 Code)"
                  value=""
                  disabled
                  dense
                  outlined
                  persistent-placeholder
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-divider class="my-4"></v-divider>
            <div class="mb-10 tw-text-base"><span class="tw-text-red-500 tw-opacity-80">*</span> สําหรับรหัสภาษี P00</div>
            <v-row class="py-0">
              <v-col cols="12" sm="6" class="py-0">
                <v-autocomplete
                  v-model="form.defaultAcctP00.expCode"
                  :items="expenseList"
                  :item-text="exp => `${exp.expCode} - ${exp.expName}`"
                  item-value="expCode"
                  label="รหัสบัญชีรายจ่าย (Expense Code)"
                  :rules="[v => !!v || 'กรุณาเลือกรหัสบัญชี']"
                  required
                  dense
                  outlined
                  persistent-placeholder
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6" class="py-0">
                <v-autocomplete
                  v-model="form.defaultAcctP00.vatCode"
                  :items="vatList"
                  :item-text="vat => `${vat.vatCode} - ${vat.vatName}`"
                  item-value="vatCode"
                  disabled
                  label="รหัสภาษี (Tax Code)"
                  :rules="[v => !!v || 'กรุณาเลือกรหัสภาษี']"
                  required
                  dense
                  outlined
                  persistent-placeholder
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row class="py-0">
              <v-col cols="12" sm="6" class="py-0">
                <v-autocomplete
                  v-model="form.defaultAcctP00.whtCode"
                  :items="whtList"
                  :item-text="wht => `${wht.whtCode} - ${wht.whtName} (${wht.whtRate}%)`"
                  item-value="whtCode"
                  label="รหัสภาษีหัก ณ ที่จ่าย (WTax Code)"
                  dense
                  outlined
                  persistent-placeholder
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions class="tw-mb-2">
        <v-spacer></v-spacer>
        <v-btn text @click="cancel" :disabled="isUpdatingVendorDefault" outlined>
          ยกเลิก
        </v-btn>
        <v-btn
          color="blue darken-1"
          @click="save"
          :disabled="!valid"
          class="tw-text-white"
          :loading="isUpdatingVendorDefault"
        >
          <v-icon left>{{ isEditMode ? "mdi-check" : "mdi-plus" }}</v-icon>
          {{ isEditMode ? "บันทึก" : "เพิ่ม" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import fc from "@/firebaseConfig";

export default {
  name: "ApVendorFormDialog",
  props: {
    isOpen: { type: Boolean, default: false },
    apVendor: { type: Object, default: () => ({}) },
    expenseList: { type: Array, required: true },
    vatList: { type: Array, required: true },
    whtList: { type: Array, required: true },
    paymentList: { type: Array, required: true },
    isEditMode: { type: Boolean, required: true },
    companyID: { type: String, required: true },
    // Which Firestore subcollection to write to. Defaults to the live vendor
    // collection; the VendorIQ tab passes "PurchaseInvoiceVendorDrafts" so
    // edits to a draft don't accidentally promote it to a real vendor.
    collectionName: { type: String, default: "PurchaseInvoiceVendors" },
  },
  computed: {
    isVendorIqDraft() {
      return this.collectionName === "PurchaseInvoiceVendorDrafts";
    },
    venCodeLabel() {
      return this.isVendorIqDraft ? "รหัสผู้จำหน่าย" : "รหัสบริษัท";
    },
    venCodeRequiredMessage() {
      return `กรุณากรอก${this.venCodeLabel}`;
    },
    venNameLabel() {
      return this.isVendorIqDraft ? "ชื่อผู้จำหน่าย" : "ชื่อบริษัท";
    },
    venNameRequiredMessage() {
      return `กรุณากรอก${this.venNameLabel}`;
    },
    dialogOpen: {
      get() {
        return this.isOpen;
      },
      set(value) {
        this.$emit("update:isOpen", value);
      },
    },
  },
  data() {
    return {
      isUpdatingVendorDefault: false,
      form: {
        venCode: "",
        venName: "",
        venPrefix: "",
        venBranch: "",
        venAddr: "",
        venTaxId: "",
        venZipCode: "",
        payCode: "",
        defaultAcctP07: {
          expCode: "",
          vatCode: "P07",
          whtCode: "",
        },
        defaultAcctP00: {
          expCode: "",
          vatCode: "P00",
          whtCode: "",
        },
        isRejected: false,
        isIncludeVat: false,
        isForeign: false,
      },
      valid: false,
    };
  },
  watch: {
    apVendor: {
      immediate: true,
      handler(newVal) {
        if (Object.keys(newVal).length > 0) {
          this.form = {
            venCode: newVal.venCode || "",
            venName: newVal.venName || "",
            venBranch: newVal.venBranch || "",
            venPrefix: newVal.venPrefix || "",
            venAddr: newVal.venAddr || "",
            venTaxId: newVal.venTaxId || "",
            venZipCode: newVal.venZipCode || "",
            payCode: newVal.payCode || "",
            defaultAcctP07: {
              expCode: newVal.defaultAcctP07.expCode || "",
              vatCode: "P07",
              whtCode: newVal.defaultAcctP07.whtCode || "",
            },
            defaultAcctP00: {
              expCode: newVal.defaultAcctP00.expCode || "",
              vatCode: "P00",
              whtCode: newVal.defaultAcctP00.whtCode || "",
            },
            isRejected: newVal.isRejected || false,
            isIncludeVat: newVal.isIncludeVat || false,
            isForeign: newVal.isForeign || false,
          };
        } else {
          this.resetForm();
        }
      },
    },
    "form.whtCode"(newValue) {
      const wht = this.whtList.find(x => x?.whtCode === newValue);
      this.form.whtRate = wht ? wht.whtRate : 0;
    },
  },
  methods: {
    resetForm() {
      this.form = {
        venCode: "",
        venName: "",
        venBranch: "",
        venPrefix: "",
        venAddr: "",
        venTaxId: "",
        venZipCode: "",
        payCode: "",
        defaultAcctP07: {
          expCode: "",
          vatCode: "P07",
          whtCode: "",
        },
        defaultAcctP00: {
          expCode: "",
          vatCode: "P00",
          whtCode: "",
        },
        isRejected: false,
        isIncludeVat: false,
        isForeign: false,
      };
      this.$refs.apVendorForm?.reset();
      this.isUpdatingVendorDefault = false;
    },
    cancel() {
      this.$emit("update:isOpen", false);
      this.resetForm();
    },
    async save() {
      if (this.$refs.apVendorForm.validate()) {
        this.isUpdatingVendorDefault = true;

        const expenseP07 = this.expenseList.find(x => x.expCode === this.form.defaultAcctP07.expCode);
        const vatP07 = this.vatList.find(x => x.vatCode === this.form.defaultAcctP07.vatCode);
        const whtP07 = this.whtList.find(x => x.whtCode === this.form.defaultAcctP07.whtCode);
        const expenseP00 = this.expenseList.find(x => x.expCode === this.form.defaultAcctP00.expCode);
        const vatP00 = this.vatList.find(x => x.vatCode === this.form.defaultAcctP00.vatCode);
        const whtP00 = this.whtList.find(x => x.whtCode === this.form.defaultAcctP00.whtCode);

        const vendorData = {
          venCode: this.form.venCode,
          venName: this.form.venName,
          venBranch: this.form.venBranch,
          venPrefix: this.form.venPrefix,
          venAddr: this.form.venAddr,
          venTaxId: this.form.venTaxId,
          venZipCode: this.form.venZipCode,
          defaultAcctP07: {
            whtCode: this.form.defaultAcctP07.whtCode,
            whtData: whtP07 || {},
            expCode: this.form.defaultAcctP07.expCode,
            expData: expenseP07 || {},
            vatCode: this.form.defaultAcctP07.vatCode,
            vatData: vatP07 || {},
          },
          defaultAcctP00: {
            whtCode: this.form.defaultAcctP00.whtCode,
            whtData: whtP00 || {},
            expCode: this.form.defaultAcctP00.expCode,
            expData: expenseP00 || {},
            vatCode: this.form.defaultAcctP00.vatCode,
            vatData: vatP00 || {},
          },
          payCode: this.form.payCode || "",
          payData: {
            payCode: this.form.payCode || "",
            payName: this.paymentList.find(p => p.payCode === this.form.payCode)?.payName || "",
            tranType: this.paymentList.find(p => p.payCode === this.form.payCode)?.tranType || "",
          },
          venSearchString: this.generateSearchString() || "",
          isRejected: this.form.isRejected || false,
          isIncludeVat: this.form.isIncludeVat || false,
          isForeign: this.form.isForeign || false,
        };

        try {
          const shouldKeepExistingDraftId =
            this.collectionName === "PurchaseInvoiceVendorDrafts" &&
            this.isEditMode &&
            this.apVendor &&
            this.apVendor.vendorId;
          const docId =
            shouldKeepExistingDraftId
              ? this.apVendor.vendorId
              : vendorData.venCode;
          const vendorRef = fc.db
            .collection(`Companies/${this.companyID}/${this.collectionName}`)
            .doc(docId);

          await vendorRef.set(vendorData, { merge: true });

          console.log("vendorData: ", vendorData);

          this.$emit("save", vendorData);
          this.cancel();
        } catch (error) {
          console.error("Error saving vendor:", error);
        } finally {
          this.isUpdatingVendorDefault = false;
        }
      }
    },
    generateSearchString() {
      const { venCode, venName, venBranch, venTaxId, venAddr, venZipCode } = this.form;
      // return `${venCode} ${venName} ${venBranch} ${venTaxId} ${venAddr} ${venZipCode}`;
      return `${venName} ${venBranch === "00000" ? venBranch : ""}  ${venTaxId}`;
    },
  },
};
</script>

<style scoped>
/* Add any necessary styles here */
</style>
